/**
 * Quick selectors (Faz 3.8 — G7): LayerCombo / ColorCombo / LinetypeCombo /
 * LineweightCombo / StyleCombo. All five share one combo shell (Chakra Menu,
 * w-max rows); each selector edits the SELECTED entities' overrides — or
 * when nothing is selected shows/sets the ACTIVE LAYER's default — following
 * the BYLAYER resolution model of rendering/entityLineStyle.
 *
 * Multi-selection with differing values shows the *VARIES* marker (Faz 5
 * schema-engine semantics, same data source).
 */

import { Box, Icon, Menu, MenuButton, MenuItem, MenuList, Text, Tooltip } from '@chakra-ui/react';
import { Check, ChevronDown, Layers } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { RibbonComboDef } from '../../../../config/ribbonTabs';
import { useAppStore } from '../../../../state/store';
import { undoableModify } from '../../../../core/commands/entityOps';
import { useDimStyleStore } from '../../../../tools/cad/DimensioningTools';
import { useLeaderStyleStore } from '../../../../state/commandAuxStores';
import { runRibbonBinding, primaryCommandName } from '../ribbonCommand';

/** Classic 27-value lineweight table (mm). */
const LINEWEIGHTS = [0, 0.05, 0.09, 0.13, 0.15, 0.18, 0.2, 0.25, 0.3, 0.35, 0.4, 0.5, 0.53, 0.6, 0.7, 0.8, 0.9, 1.0, 1.06, 1.2, 1.4, 1.58, 2.0, 2.11, 2.5, 3.0, 3.5];

const LINETYPES = ['BYLAYER', 'BYBLOCK', 'CONTINUOUS', 'DASHED', 'HIDDEN', 'CENTER', 'PHANTOM', 'DASHDOT', 'DOTTED'] as const;

/** Compact ACI subset (the standard 9 + a few frequently used indices).
 *  ByBlock is intentionally absent: the renderer has no BYBLOCK color
 *  resolution yet (entityLineStyle covers linetype/lineweight only) — a
 *  BYBLOCK override would render as an invalid color. */
const ACI_COLORS: { aci: number; hex: string }[] = [
    { aci: 256, hex: '#8ea3b0' }, // ByLayer
    { aci: 1, hex: '#ff0000' },
    { aci: 2, hex: '#ffff00' },
    { aci: 3, hex: '#00ff00' },
    { aci: 4, hex: '#00ffff' },
    { aci: 5, hex: '#0000ff' },
    { aci: 6, hex: '#ff00ff' },
    { aci: 7, hex: '#ffffff' },
    { aci: 8, hex: '#808080' },
    { aci: 9, hex: '#c0c0c0' },
    { aci: 30, hex: '#ff7f00' },
    { aci: 40, hex: '#ffbf00' },
    { aci: 50, hex: '#7fff00' },
    { aci: 90, hex: '#007fff' },
    { aci: 130, hex: '#7f00ff' },
    { aci: 170, hex: '#ff007f' },
    { aci: 210, hex: '#5e5e5e' },
];

const BYLAYER_ACI = 'BYLAYER';
const BYBLOCK_ACI = 'BYBLOCK';

function aciLabel(aci: number): string {
    if (aci === 256) return 'ByLayer';
    if (aci === 0) return 'ByBlock';
    return String(aci);
}

/** VARIES marker for multi-selections with different values. */
const VARIES = '*VARIES*';

/** Read the effective value of one override field across the selection. */
function selectionField(field: 'color' | 'linetype' | 'lineWeight'): { value: unknown; varies: boolean; hasSelection: boolean } {
    const { entities, selectedIds, layers, activeLayerId } = useAppStore.getState();
    const ids = selectedIds.filter((id) => entities[id]);
    if (ids.length === 0) {
        return { value: undefined, varies: false, hasSelection: false };
    }
    const layer = layers[entities[ids[0]].layer] ?? layers[activeLayerId];
    const read = (id: string): unknown => {
        const d = entities[id].data as Record<string, unknown>;
        if (field === 'color') {
            if (d.color !== undefined) return String(d.color);
            return layer?.color ?? '#ffffff';
        }
        if (field === 'linetype') {
            if (typeof d.linetype === 'string' && d.linetype !== 'BYLAYER') return d.linetype;
            return layer?.linetype ?? 'CONTINUOUS';
        }
        if (typeof d.lineWeight === 'number') return d.lineWeight;
        return layer?.lineweight ?? 25;
    };
    const first = read(ids[0]);
    const varies = ids.some((id) => String(read(id)) !== String(first));
    return { value: first, varies, hasSelection: true };
}

/** Write one override field onto the selection (single undoable step). */
function applyOverride(field: 'color' | 'linetype' | 'lineWeight', value: unknown): void {
    const { entities, selectedIds } = useAppStore.getState();
    const ids = selectedIds.filter((id) => entities[id]);
    if (ids.length === 0) {
        // No selection: edit the ACTIVE LAYER's default instead (AutoCAD
        // current-property semantics).
        const app = useAppStore.getState();
        const layerId = app.activeLayerId;
        if (field === 'color') app.updateLayer(layerId, { color: String(value) });
        if (field === 'linetype') app.updateLayer(layerId, { linetype: value === 'CONTINUOUS' ? undefined : String(value) });
        if (field === 'lineWeight') app.updateLayer(layerId, { lineweight: Number(value) });
        return;
    }
    undoableModify(ids, (data) => {
        if (field === 'color') return { ...data, color: value === BYLAYER_ACI ? undefined : value };
        if (field === 'linetype') return { ...data, linetype: value === 'BYLAYER' ? 'BYLAYER' : value };
        return { ...data, lineWeight: value === 'BYLAYER' ? 'BYLAYER' : Number(value) };
    });
}

/** Shared combo chrome: labeled pill button + w-max menu. */
function ComboShell({ buttonLabel, tooltip, swatch, children, width = 120 }: {
    buttonLabel: string;
    tooltip: string;
    swatch?: string | null;
    children: React.ReactNode;
    width?: number;
}) {
    return (
        <Menu closeOnSelect autoSelect={false}>
            <Tooltip label={tooltip} placement="bottom" openDelay={400} hasArrow>
                <MenuButton
                    as={Box}
                    role="button"
                    display="flex"
                    alignItems="center"
                    gap="4px"
                    h="24px"
                    px={1}
                    minW={`${width}px`}
                    maxW={`${width + 40}px`}
                    flexShrink={0}
                    borderRadius="4px"
                    border="1px solid"
                    borderColor="border.base"
                    bg="bg.panel"
                    _hover={{ bg: 'bg.hover' }}
                    _expanded={{ bg: 'bg.hover' }}
                >
                    {swatch !== undefined && swatch !== null && (
                        <Box w="10px" h="10px" borderRadius="2px" border="1px solid" borderColor="border.base" bg={swatch} flexShrink={0} />
                    )}
                    <Text fontSize="11px" color="text.primary" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                        {buttonLabel}
                    </Text>
                    <Icon as={ChevronDown} boxSize="10px" color="text.secondary" ml="auto" flexShrink={0} />
                </MenuButton>
            </Tooltip>
            <MenuList w="max-content" maxH="280px" overflowY="auto" bg="bg.panel" borderColor="border.base">
                {children}
            </MenuList>
        </Menu>
    );
}

function MarkedRow({ label, selected, onClick, swatch, dashed }: {
    label: string;
    selected: boolean;
    onClick: () => void;
    swatch?: string;
    dashed?: boolean;
}) {
    return (
        <MenuItem
            onClick={onClick}
            fontSize="12px"
            color="text.primary"
            icon={
                <Box display="flex" alignItems="center" w="16px">
                    {selected ? <Icon as={Check} boxSize="12px" color="brand.500" /> : swatch ? (
                        <Box
                            w="12px"
                            h="0"
                            borderTop={dashed ? '2px dashed' : '2px solid'}
                            borderColor={swatch}
                        />
                    ) : null}
                </Box>
            }
        >
            {label}
        </MenuItem>
    );
}

/* --------------------------- LayerCombo --------------------------- */

function LayerCombo() {
    const { t } = useTranslation();
    const layers = useAppStore((s) => s.layers);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const setActiveLayer = useAppStore((s) => s.setActiveLayer);
    const sorted = useMemo(
        () => Object.values(layers).sort((a, b) => a.order - b.order),
        [layers]
    );
    const active = layers[activeLayerId];

    return (
        <ComboShell
            buttonLabel={active?.name ?? '0'}
            tooltip={t('ribbon.quick.layerTooltip')}
            swatch={active?.color}
        >
            {sorted.map((layer) => (
                <MarkedRow
                    key={layer.id}
                    label={`${layer.name}${layer.frozen ? ' ❄' : ''}${layer.locked ? ' 🔒' : ''}${!layer.visible ? ' 🚫' : ''}`}
                    selected={layer.id === activeLayerId}
                    swatch={layer.color}
                    onClick={() => setActiveLayer(layer.id)}
                />
            ))}
            <MenuItem
                fontSize="12px"
                color="text.primary"
                icon={<Icon as={Layers} boxSize="12px" color="icon.base" />}
                onClick={() => {
                    const app = useAppStore.getState();
                    if (!app.sidebarOpen) app.toggleSidebar();
                }}
            >
                {t('ribbon.quick.layerProperties')}
            </MenuItem>
        </ComboShell>
    );
}

/* --------------------------- ColorCombo --------------------------- */

function ColorCombo() {
    const { t } = useTranslation();
    const read = selectionField('color');
    const currentHex = typeof read.value === 'string' && read.value.startsWith('#') ? read.value : undefined;
    const currentAci = ACI_COLORS.find((c) => c.hex.toLowerCase() === currentHex?.toLowerCase());

    return (
        <ComboShell
            buttonLabel={read.varies ? VARIES : currentAci ? aciLabel(currentAci.aci) : (currentHex ?? 'ByLayer')}
            tooltip={t('ribbon.quick.colorTooltip')}
            swatch={read.varies ? undefined : currentHex ?? '#8ea3b0'}
            width={86}
        >
            {ACI_COLORS.map((c) => (
                <MarkedRow
                    key={c.aci}
                    label={`${aciLabel(c.aci)}`}
                    selected={!read.varies && currentAci?.aci === c.aci}
                    swatch={c.hex}
                    onClick={() => applyOverride('color', c.aci === 256 ? BYLAYER_ACI : c.hex)}
                />
            ))}
        </ComboShell>
    );
}

/* ------------------------- LinetypeCombo -------------------------- */

function LinetypeCombo() {
    const { t } = useTranslation();
    const read = selectionField('linetype');
    const current = typeof read.value === 'string' ? read.value : 'CONTINUOUS';

    return (
        <ComboShell
            buttonLabel={read.varies ? VARIES : current === 'BYLAYER' ? 'ByLayer' : current}
            tooltip={t('ribbon.quick.linetypeTooltip')}
            width={96}
        >
            {LINETYPES.map((lt) => (
                <MarkedRow
                    key={lt}
                    label={lt === 'BYLAYER' ? 'ByLayer' : lt === 'BYBLOCK' ? 'ByBlock' : lt}
                    selected={!read.varies && current === lt}
                    dashed={lt !== 'CONTINUOUS' && lt !== 'BYLAYER' && lt !== 'BYBLOCK'}
                    swatch="text.secondary"
                    onClick={() => applyOverride('linetype', lt)}
                />
            ))}
        </ComboShell>
    );
}

/* ------------------------ LineweightCombo ------------------------- */

function LineweightCombo() {
    const { t } = useTranslation();
    const read = selectionField('lineWeight');
    const currentNum = typeof read.value === 'number' ? read.value : undefined;

    return (
        <ComboShell
            buttonLabel={read.varies ? VARIES : currentNum !== undefined ? `${currentNum.toString().replace(/\.0+$/, '')}.00 mm` : 'ByLayer'}
            tooltip={t('ribbon.quick.lineweightTooltip')}
            width={92}
        >
            <MarkedRow
                label="ByLayer"
                selected={!read.varies && currentNum === undefined}
                onClick={() => applyOverride('lineWeight', 'BYLAYER')}
            />
            {LINEWEIGHTS.map((lw) => (
                <MarkedRow
                    key={lw}
                    label={`${lw.toString().replace(/\.0+$/, '')}.00 mm`}
                    selected={!read.varies && currentNum === lw}
                    onClick={() => applyOverride('lineWeight', lw)}
                />
            ))}
        </ComboShell>
    );
}

/* -------------------------- StyleCombo ---------------------------- */

function StyleCombo({ item }: { item: RibbonComboDef }) {
    const { t } = useTranslation();
    const dimStyles = useDimStyleStore((s) => s.styles);
    const dimActiveId = useDimStyleStore((s) => s.activeStyleId);
    const setDimActive = useDimStyleStore((s) => s.setActiveStyle);
    const leaderStyles = useLeaderStyleStore((s) => s.styles);
    const leaderActiveId = useLeaderStyleStore((s) => s.activeId);
    const setLeaderActive = useLeaderStyleStore((s) => s.setActive);

    const isDim = item.styleKey === 'dimstyle';
    const activeName = isDim
        ? dimStyles[dimActiveId]?.name ?? 'Standard'
        : leaderStyles[leaderActiveId]?.name ?? 'Standard';

    return (
        <ComboShell
            buttonLabel={activeName}
            tooltip={isDim ? t('ribbon.quick.dimStyleTooltip') : t('ribbon.quick.mleaderStyleTooltip')}
            width={110}
        >
            {isDim
                ? Object.values(dimStyles).map((st) => (
                    <MarkedRow
                        key={st.id}
                        label={st.name}
                        selected={st.id === dimActiveId}
                        onClick={() => setDimActive(st.id)}
                    />
                ))
                : Object.values(leaderStyles).map((st) => (
                    <MarkedRow
                        key={st.id}
                        label={st.name}
                        selected={st.id === leaderActiveId}
                        onClick={() => setLeaderActive(st.id)}
                    />
                ))}
            {item.managerCmd && (
                <MenuItem
                    fontSize="12px"
                    color="text.primary"
                    icon={<Icon as={Layers} boxSize="12px" color="icon.base" />}
                    onClick={() => item.managerCmd && runRibbonBinding({ cmdId: item.managerCmd })}
                >
                    {`${t('ribbon.quick.styleManager')} (${item.managerCmd ? primaryCommandName(item.managerCmd) : ''})`}
                </MenuItem>
            )}
        </ComboShell>
    );
}

/* --------------------------- dispatcher --------------------------- */

export function RibbonCombo({ item }: { item: RibbonComboDef }) {
    switch (item.kind) {
        case 'layerCombo': return <LayerCombo />;
        case 'colorCombo': return <ColorCombo />;
        case 'linetypeCombo': return <LinetypeCombo />;
        case 'lwCombo': return <LineweightCombo />;
        case 'styleCombo': return <StyleCombo item={item} />;
        default: return null;
    }
}
